import React, { useContext, useEffect, useRef } from 'react';

import altBackground from '../../assets/images/events/goal-background.png';
import { LbEvent, events, LbEventType } from '../../configs/events';
import { AmpPropsContext } from '../../context/amp-props/amp-props-provider';
import { ComponentProps } from '../../typings/generic-component';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { IClassNames, makeClassNames } from '../../utils/styles';
import { getDateTime } from '../../utils/times';
import EventIcon from '../event-icon/event-icon';
import { AcceptedCookiesConfig } from '../liveblog/liveblog';
import PostImage, { IPostImage } from '../post-image/post-image';
import PostVideo, { IPostVideo } from '../post-video/post-video';
import SocialEmbed, { ISocialEmbed } from '../social-embed/social-embed';
import './post.scss';

export interface IPost extends ComponentProps {
  postId: string;
  isPinned?: boolean;
  title?: string;
  content?: string;
  timestamp: string; // ISO format like '2022-10-18T09:01:22.423Z'
  timestampUtcIt?: string; // ISO format like '2022-10-18T11:01:22.423+02:00'
  event?: LbEvent;
  altBackground?: boolean;
  authorName?: string;
  video?: IPostVideo;
  image?: IPostImage;
  social?: ISocialEmbed;
  dateNow: Date;
  acceptedCookiesConfig: AcceptedCookiesConfig;
  tenant?: LiveblogTenant;
  isWebView?: boolean;
}

const classNames = (
  event?: LbEvent,
  altBackground?: boolean,
  isWebView?: boolean,
  isGol?: boolean,
  isCheckedFlag?: boolean,
  isVideoOrImage?: boolean
): IClassNames =>
  makeClassNames({
    Post: 'post',
    PostTimestamp: 'post__timestamp',
    PostTimestampTime: 'post__timestamp-time',
    PostTimestampDate: 'post__timestamp-date',
    Post_Wrapper: 'post__wrapper',
    Post_Icon: 'post__icon',
    Post_Container: 'post__container',
    Post_Title: [(isGol || isCheckedFlag) && 'post__title-alt', 'post__title-v2'],
    Post_Content: [
      event && 'post__event-post-content',
      (isGol || isCheckedFlag) && ' post__event-post-content-alt post__content-alt',
      altBackground && !event && !isWebView && 'post__content--with-alt-background',
      'post__content-v2',
    ],
    Post_ContentText: 'post__content-text-v2',
    Post_Body: [(isGol || isCheckedFlag) && isVideoOrImage && 'post__body-alt', 'post__body'],
    Post_Event_Container: 'post__event__container',
    Post_Event_IconTitle: [
      isGol && 'post__event__icon-title-gol',
      isCheckedFlag && 'post__event__icon-title-checked',
      event?.type === LbEventType.SPECIAL_EVENTS && 'post__event__icon-title-capitalizeFirstLetter',
      'post__event__icon-title',
    ],
    Post_Author: 'post__author',
    Post_AuthorName: 'post__author-name',
    Post_Footer: 'post__footer',
  });

const altBackgroundStyles = {
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundImage: `url(${altBackground})`,
  color: 'white',
};

const Post: React.FC<IPost> = ({
  postId,
  content = '',
  title,
  timestamp,
  timestampUtcIt,
  event,
  altBackground = false,
  video,
  image,
  social,
  dateNow,
  acceptedCookiesConfig,
  tenant,
  isWebView,
}) => {
  const postContentRef = useRef<HTMLDivElement | null>(null);
  const LMP_BORDER_COLOR = event?.borderColor || '';
  const isGol: boolean = event?.text === events?.goal.text;
  const isCheckedFlag: boolean = event?.text === events?.['bandiera a scacchi'].text;
  const isVideoOrImage: boolean = !!(video || image);
  const { isAmp } = useContext(AmpPropsContext);
  const { time, date } = getDateTime({ timestamp, dateNow, isAmp });
  const { socialType, src: socialSrc } = social || {};

  const cn = classNames(event, altBackground, isWebView, isGol, isCheckedFlag, isVideoOrImage);

  useEffect(() => {
    if (event && postContentRef.current) {
      const isAltBackgroundPost = isGol || isCheckedFlag;
      postContentRef.current.style.setProperty(
        '--lmpBorderColor',
        isAltBackgroundPost ? (event?.backgroundColor as string) : LMP_BORDER_COLOR
      );
      if (isAltBackgroundPost) {
        Object.assign(postContentRef.current.style, altBackgroundStyles);
      }
    }
  }, []);

  return (
    <div className={cn.Post}>
      <div className={cn.PostTimestamp}>
        <time dateTime={timestampUtcIt || timestamp} className={cn.PostTimestampTime}>
          {time}
        </time>
        {date && (
          <time dateTime={timestampUtcIt || timestamp} className={cn.PostTimestampDate}>
            {date}
          </time>
        )}
      </div>
      <div className={cn.Post_Wrapper}>
        <div className={cn.Post_Container}>
          <div ref={postContentRef} className={cn.Post_Content}>
            {event && (
              <div className={cn.Post_Event_Container}>
                <EventIcon {...event} />
                <span className={cn.Post_Event_IconTitle}>{event.text}</span>
              </div>
            )}
            <div className={cn.Post_ContentText}>
              <h2 className={cn.Post_Title}>{title}</h2>
              <div className={cn.Post_Body}>
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            </div>
            {video && (
              <PostVideo
                {...video}
                containerId={`lvbg-video-player-post-${postId}`}
                autostart={false}
                tenant={tenant}
                isWebView={isWebView}
              />
            )}
            {image?.imgSrc && <PostImage {...image} />}
            {socialType && socialSrc && (
              <SocialEmbed
                {...social}
                src={socialSrc}
                cookieAllowed={acceptedCookiesConfig[socialType]}
                isWebView={isWebView}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Post;
